import React from 'react';
import { Loader2, Save, X, AlertCircle } from 'lucide-react';

interface SaveBarProps {
  dirty: boolean;
  saving?: boolean;
  /** Optional last-saved timestamp; renders "Saved Xs ago" hint. */
  savedAt?: number | null;
  /** Override default labels. */
  saveLabel?: string;
  cancelLabel?: string;
  onSave: () => void;
  onCancel?: () => void;
  /** Optional extra status / inline error. */
  hint?: React.ReactNode;
  /** Disable save (e.g. validation blocks it). */
  disabled?: boolean;
}

/**
 * Floating bottom-right Save bar. Always visible while dirty.
 * Companion to useDirtyState — the hook installs the beforeunload guard.
 */
export const SaveBar: React.FC<SaveBarProps> = ({
  dirty,
  saving,
  savedAt,
  saveLabel = 'Save changes',
  cancelLabel = 'Discard',
  onSave,
  onCancel,
  hint,
  disabled = false,
}) => {
  if (!dirty && !saving && !savedAt) return null;

  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      style={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: 'var(--z-popover, 1100)',
        background: 'var(--surface-elev, var(--surface))',
        border: `1px solid ${dirty ? 'var(--warning)' : 'var(--border)'}`,
        borderRadius: 'var(--r-3)',
        boxShadow: 'var(--shadow-lg)',
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        maxWidth: 'min(96vw, 540px)',
        animation: 'savebar-pop 180ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12.5,
          fontWeight: 500,
          color: dirty ? 'var(--warning)' : 'var(--fg-2)',
        }}
      >
        {dirty
          ? <><AlertCircle size={14} /> Unsaved changes</>
          : saving
            ? <><Loader2 size={14} className="spin" /> Saving…</>
            : <span style={{ color: 'var(--fg-3)' }}>All changes saved</span>}
      </span>
      {hint && (
        <span style={{ fontSize: 12, color: 'var(--fg-3)', marginRight: 'auto' }}>
          {hint}
        </span>
      )}
      <div style={{ display: 'inline-flex', gap: 8, marginLeft: hint ? 0 : 'auto' }}>
        {onCancel && dirty && (
          <button
            type="button"
            className="hf-btn ghost"
            onClick={onCancel}
            disabled={saving}
          >
            <X size={13} /> {cancelLabel}
          </button>
        )}
        <button
          type="button"
          className="hf-btn primary"
          onClick={onSave}
          disabled={saving || disabled || !dirty}
        >
          {saving
            ? <><Loader2 size={14} className="spin" /> Saving…</>
            : <><Save size={14} /> {saveLabel}</>}
        </button>
      </div>
    </div>
  );
};
